/* @refresh reset */
// Copied from transition-web/src/hooks/useAppState.tsx
// Enhanced with global Firebase auth sync

import * as auth from 'firebase/auth'
import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react'
import { User } from '../../../pure-js/types/User'
import { auth as firebaseAuth } from '../libs/Firebase'
import { getUser } from '../libs/DBApiHandlerDeprecated'

export type State = {
  user: User
  firebaseUser?: auth.User
  shouldShowExpandedMenu?: boolean
  settingsTabIndex?: number
  shouldShowCall?: boolean
  currentOnboardingChapter?: any
  isAuthInitialized?: boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_STATE: State = {
  user: { id: '' } as User,
  shouldShowExpandedMenu: false,
  shouldShowCall: false,
  isAuthInitialized: false
}

export type AppContext = {
  state: State
  setState: Dispatch<SetStateAction<State>>
}

// eslint-disable-next-line react-refresh/only-export-components
export const Context = React.createContext<AppContext>({
  state: DEFAULT_STATE,
  setState: () => ({})
})

// eslint-disable-next-line react-refresh/only-export-components
export default function useAppState() {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider')
  }
  return context
}

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<State>(DEFAULT_STATE)

  // Global Firebase auth state listener - syncs Firebase auth with app state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        // No Firebase user - ensure app state is cleared
        if (state.user?.id && state.user.id !== '') {
          setState({ ...DEFAULT_STATE, isAuthInitialized: true })
        } else {
          // Just mark auth as initialized if state was already clear
          setState((prev) => ({ ...prev, isAuthInitialized: true }))
        }
        return
      }

      // Firebase user exists but app state doesn't have the user (e.g. another tab
      // switched accounts). Hydrate the user doc here so every tab/route recovers,
      // not only the login page — otherwise pages like /signedin get stuck.
      if (!state.user?.id || state.user.id === '') {
        try {
          // Mint the ID token before the Firestore read — reading inside the auth callback can race token propagation and trigger a permission-denied.
          await firebaseUser.getIdToken()
          const user = await getUser(firebaseUser.uid)
          // Guard against a slow fetch resolving after the auth user changed
          // (e.g. a fast account switch) — never apply a stale/other-account user.
          if (firebaseAuth.currentUser?.uid !== firebaseUser.uid) return
          if (user?.id) {
            setState((prev) => ({ ...prev, user, firebaseUser, isAuthInitialized: true }))
            return
          }
        } catch {
          // Fall through — mark initialized so ProtectedRoute can route to /login.
        }
        setState((prev) => ({ ...prev, firebaseUser, isAuthInitialized: true }))
      } else {
        setState((prev) => ({ ...prev, firebaseUser, isAuthInitialized: true }))
      }
    })

    return () => {
      unsubscribe()
    }
  }, [state.user?.id])

  return <Context.Provider value={{ state, setState }}>{children}</Context.Provider>
}
